import {
  TRANSACTIONS_TAG, TRANSACTIONS_BALLANCE_TAG, TRANSACTIONS_ROUTE, ORDERS_TAG, USER_ORDERS_TAG,
} from '@lib/actions/api/wallet/tags';
import { Dispatch, SetStateAction } from 'react';
import { ApiOrderSaveOrder } from '@type/v1-api-types/order';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrGlobal } from '@hooks/useSwrApi';
import { revalidateTagAction } from '@lib/actions/global';
import {
  useSwrOrderCreateCart,
  useSwrMutationOrderUpdateCart,
  useSwrOrderContinue,
  useSwrSaveOrder,
  useSwrOrderDeleteCart,
} from '@hooks/useSwrApi/endpoints/order';


/**
 * @description Hook interface.
 */
interface UseBuyNeuroProps {
  stepSetter?: Dispatch<SetStateAction<string>>,
  externalCartId?: number | string,
}


/**
 * @description Hook to buy Neuro.
 * - `stepSetter` - function to set the flow step.
 * - `externalCartId` - the external cart id. If you use the hook in a different context (ie: a page with id as a param),
 * you can provide the cart id here.
 */
export const useBuyNeuro = (props?: UseBuyNeuroProps) => {
  // Destructure props
  const { stepSetter, externalCartId } = props || {};

  // Router
  const { locale, pathname, router } = useExtendedRouter();

  // SWR Global
  const { revalidateKey } = useSwrGlobal();

  // Go to the PaymentMethods step on success
  const goToPaymentMethods = () => {
    if (stepSetter) stepSetter('PaymentMethods');
  };

  // Handle the order error
  const handleOrderError = () => {
    if (stepSetter) stepSetter('Error');
  };

  // - If the order is completed and paid, show success step.
  // - If the order is completed but not paid (payment was by invoice), show success step + link to invoice.
  // - If the order is not completed, show failed step.
  // ****************************************************
  const handleOrderSuccess = async (data: ApiOrderSaveOrder) => {
    let step: string | undefined;
    // Check the order status
    if (data?.completed) {
      step = data?.paid ? 'CompletedCard' : 'CompletedInvoice';
      // Invalidate the Transactions cache & header-info cache.
      await revalidateTagAction(TRANSACTIONS_TAG);
      await revalidateTagAction(TRANSACTIONS_BALLANCE_TAG);
      await revalidateTagAction(ORDERS_TAG);
      await revalidateTagAction(USER_ORDERS_TAG);
      // Refresh the transactions page if we are on it. Give the backend time to process the order
      if (pathname && pathname.startsWith(TRANSACTIONS_ROUTE)) router.refresh();
      setTimeout(() => revalidateKey('header-info'), 1000);
    } else if (data?.checkoutSessionUrl) {
      window.location.replace(data.checkoutSessionUrl);
      return;
    } else {
      step = 'NotCompleted';
    }
    // Set the step
    if (stepSetter) stepSetter(step);
  };


  // ****************************************************
  // Data fetching
  // ****************************************************
  // SWR: Create shopping cart
  const {
    data: shoppingCartId,
    trigger: createShoppingCart,
    isMutating: loadingCartId,
  } = useSwrOrderCreateCart({
    locale,
    afterLoginRedirectPath: pathname || '/',
  });

  // SWR: Get shopping cart details
  // This is super simple - will auto-trigger when the shopping cart id is available, no action needed
  const {
    data: cartDetails,
    isLoading: loadingCartDetails,
  } = useSwrOrderContinue({
    locale,
    orderId: externalCartId ?? shoppingCartId?.id,
    successCallback: goToPaymentMethods,
  });

  // SWR: Update shopping cart details. This is used to update the credits amount.
  const {
    trigger: updateShoppingCart,
    data: cartDetailsUpdated,
    isMutating: loadingUpdatedCartDetails,
  } = useSwrMutationOrderUpdateCart({
    locale,
    orderId: externalCartId ?? shoppingCartId?.id,
    successCallback: goToPaymentMethods,
  });

  // SWR: Complete order
  const {
    trigger: orderCredits,
    data: orderResponse,
    isMutating: savingOrder,
  } = useSwrSaveOrder({
    locale,
    shoppingCartId: externalCartId ?? shoppingCartId?.id,
    successCallback: handleOrderSuccess,
    errorCallback: handleOrderError,
  });

  // Swr: Delete shopping cart
  const {
    trigger: deleteShoppingCart,
  } = useSwrOrderDeleteCart({
    locale,
    shoppingCartId: externalCartId ?? shoppingCartId?.id,
  });


  // ****************************************************
  // Return
  // ****************************************************
  return {
    createShoppingCart,
    shoppingCartId,
    loadingCartId,
    cartDetails,
    loadingCartDetails,
    updateShoppingCart,
    cartDetailsUpdated,
    loadingUpdatedCartDetails,
    deleteShoppingCart: () => { void deleteShoppingCart({ data: externalCartId ?? shoppingCartId?.id }); },
    orderCredits,
    orderResponse,
    savingOrder,
    paths: {
      toInvoice: orderResponse && orderResponse?.completed && orderResponse?.paid === false && orderResponse?.orderId
        ? () => router.push(`/wallet/order/${orderResponse.orderId}`)
        : undefined,
      notCompleted: orderResponse && orderResponse?.completed === false && orderResponse?.orderId
        ? () => router.push(`/order/checkout/${orderResponse.orderId}`)
        : undefined,
    },
  };
};
